import React from "react";
import { Button } from "@/components/ui";
import { TagIcon } from "lucide-react";
import { PostDetails } from "@/services/bedrock";

interface Props {
  view: "review" | "mentions" | "success";
  post?: PostDetails;
  withWritePermission: boolean;
  hasMentionsFeature: boolean;
  mentionsCount: number;
  isSaving: boolean;
  isDeletingAll: boolean;
  onClose: () => void;
  onSetView: (view: "review" | "mentions" | "success") => void;
  onReschedule: () => void;
  onDeleteAll: () => void;
  onSaveMentions: () => void;
}

export function PostDetailsActions({
  view,
  post,
  withWritePermission,
  hasMentionsFeature,
  mentionsCount,
  isSaving,
  isDeletingAll,
  onClose,
  onSetView,
  onReschedule,
  onDeleteAll,
  onSaveMentions,
}: Props) {
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = React.useState(false);

  // Reset confirmation state when view changes or post changes
  React.useEffect(() => {
    setShowDeleteAllConfirm(false);
  }, [view, post?.id]);

  if (view === "success") {
    return (
      <Button onClick={onClose} className="min-w-[100px]">
        Close
      </Button>
    );
  }

  if (view === "mentions") {
    return (
      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => onSetView("review")} disabled={isSaving}>
          Back
        </Button>
        <Button
          onClick={onSaveMentions}
          className="min-w-[100px]"
          disabled={isSaving || !withWritePermission}
          loading={isSaving}
        >
          Save Mentions
        </Button>
      </div>
    );
  }

  // view === "review"
  return (
    <div className="flex gap-2">
      {post?.scheduledStatus === "PENDING" && (
        <>
          {!showDeleteAllConfirm ? (
            <>
              <Button
                variant="destructive"
                disabled={!withWritePermission}
                onClick={() => setShowDeleteAllConfirm(true)}
              >
                Delete From Schedule
              </Button>

              <Button variant="outline" disabled={!withWritePermission} onClick={onReschedule}>
                Re-schedule
              </Button>
            </>
          ) : (
            <div className="flex gap-2 items-center bg-destructive/5 px-3 py-1.5 rounded-xl border border-destructive/20 animate-in fade-in zoom-in-95">
              <span className="text-xs font-bold text-destructive uppercase tracking-tight mr-2">Confirm Delete?</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs hover:bg-white"
                onClick={() => setShowDeleteAllConfirm(false)}
                disabled={isDeletingAll}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-8 text-xs"
                onClick={onDeleteAll}
                loading={isDeletingAll}
              >
                Yes, Remove from Schedule
              </Button>
            </div>
          )}
        </>
      )}
      {!showDeleteAllConfirm && (
        <>
          {post && hasMentionsFeature && (
            <Button
              variant="ghost"
              onClick={() => onSetView("mentions")}
              disabled={!withWritePermission}
              className="gap-2"
            >
              <TagIcon className="size-4" />
              Update Mentions
              {mentionsCount > 0 && <span className="text-xs text-muted-foreground">({mentionsCount})</span>}
            </Button>
          )}
          <Button onClick={onClose} className="min-w-[100px]">
            Close
          </Button>
        </>
      )}
    </div>
  );
}
